import React, { useState, useEffect } from "react";
import ReactCountryFlag from "react-country-flag";
import axiosInstance from "../axiosInterceptor";
import {
  FaExchangeAlt,
  FaTags,
  FaDownload,
  FaCopy,
  FaFileExcel,
  FaCog,
  FaBoxOpen
} from "react-icons/fa";
import { toast } from "react-toastify";
import "./dashboard.css";

const currencies = [
  { code: "INR", country: "IN" },
  { code: "USD", country: "US" },
  { code: "NZD", country: "NZ" },
  { code: "GBP", country: "GB" },
  { code: "CAD", country: "CA" },
  { code: "EUR", country: "EU" },
  { code: "AED", country: "AE" },
  { code: "AUD", country: "AU" },
];

const Dashboard = () => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [loading, setLoading] = useState(null);
  const [files, setFiles] = useState({});
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [abandonedCheckoutLoading, setAbandonedCheckoutLoading] = useState(false);
  // const [orderLoading, setOrderLoading] = useState(false);
  // const [orderLoading] = useState(false);
  const [dots, setDots] = useState("");
  const [confirmType, setConfirmType] = useState(null); // "currency" | "product" | null

  const resetRow = (code) => {
    setFiles((prev) => {
      const updated = { ...prev };
      delete updated[code];
      return updated;
    });
  };

  const generateFile = async (currency) => {
    try {
      setLoading(currency);

      const { data } = await axiosInstance.get(`${baseUrl}api/shopify/getExcelProducts?currency=${currency}`);

      if (data.success) {
        setFiles((prev) => ({
          ...prev,
          [currency]: data.file,
        }));
      }
    } catch (err) {
      alert("Something went wrong");
    } finally {
      setLoading(null);
    }
  };

  const downloadFile = (url, code) => {
    window.open(url, "_blank");
    toast.success("Download started");
    resetRow(code);
  };


  const copyUrl = async (url, code) => {
    try {
      await navigator.clipboard.writeText(url);

      toast.success("File URL copied to clipboard");

      // reset UI after copy
      resetRow(code);
    } catch (err) {
      toast.error("Failed to copy URL");
    }
  };


  const handleGetCurrency = async () => {
    try {
      setCurrencyLoading(true);

      const { data } = await axiosInstance.get(`${baseUrl}api/shopify/getCurrency`);
      if (data.success) {
        toast.success(data.message || "Currency fetched successfully");
      } else {
        toast.error(data.message || "Failed to fetch currency");
      }
    } catch (error) {
      toast.error("Something went wrong while fetching currency");
    } finally {
      setCurrencyLoading(false);
    }
  };

  const handleGetProduct = async () => {
    try {
      setProductLoading(true);

      const { data } = await axiosInstance.get(`${baseUrl}api/shopify/getProducts`);

      if (data.success) {
        toast.success(data.message || "Products fetched successfully");
      } else {
        toast.error(data.message || "Failed to fetch products");
      }
    } catch (error) {
      toast.error("Something went wrong while fetching products");
    } finally {
      setProductLoading(false);
    }
  };

  const handleGetAbandonedCheckouts = async () => {
    try {
      setAbandonedCheckoutLoading(true);

      const { data } = await axiosInstance.get(`${baseUrl}api/shopify/getAbandonedCheckouts`);

      if (data.success) {
        toast.success(data.message || "Abandoned Checkout Listed successfully");
      } else {
        toast.error(data.message || "Failed to fetch Abandoned Checkout");
      }
      // toast.success("Abandoned Checkout Listed Comming Soon...");
    } catch (error) {
      toast.error("Something went wrong while fetching products");
    } finally {
      setAbandonedCheckoutLoading(false);
    }
  };

  useEffect(() => {
    if (currencyLoading || productLoading || abandonedCheckoutLoading) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length === 3 ? "" : prev + "."));
      }, 500);

      return () => clearInterval(interval);
    } else {
      setDots("");
    }
  }, [currencyLoading, productLoading, abandonedCheckoutLoading]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setConfirmType(null);
      }
    };

    if (confirmType) {
      window.addEventListener("keydown", handleEsc);
    }

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [confirmType]);

  return (
    <div className="dashboard">
      {/* Top Actions */}
      <div className="dashboard-actions">
        <div
          className={`action-card ${currencyLoading ? "disabled" : ""}`}
          onClick={() => !currencyLoading && setConfirmType("currency")}
        >
          <FaExchangeAlt
            className={`action-icon ${currencyLoading ? "spinning" : ""}`}
          />
          <h4>{currencyLoading ? `Syncing${dots}` : "Get Currency"}</h4>
        </div>


        <div
          className={`action-card ${productLoading ? "disabled" : ""}`}
          onClick={() => !productLoading && setConfirmType("product")}
        >
          <FaTags
            className={`action-icon ${productLoading ? "spinning" : ""}`}
          />
          <h4>{productLoading ? `Syncing${dots}` : "Get Product"}</h4>
        </div>

        {/* NEW CARD */}
        <div
          className={`action-card ${abandonedCheckoutLoading ? "disabled" : ""}`}
          onClick={() => !abandonedCheckoutLoading && setConfirmType("checkout")}
        >
          <FaBoxOpen
            className={`action-icon ${abandonedCheckoutLoading ? "spinning" : ""}`}
          />
          <h4>{abandonedCheckoutLoading ? `Syncing${dots}` : "Get Abandoned Checkouts"}</h4>
        </div>

      </div>

      <div className="download-section">
        <h2>
          <FaDownload /> Download Files
        </h2>

        <div className="download-list">
          {currencies.map((item) => (
            <div key={item.code} className="download-row">

              {/* Left */}
              <div className="download-left">
                <ReactCountryFlag
                  svg
                  countryCode={item.country}
                  className="currency-flag"
                />

                <span className="code">{item.code}</span>

                {files[item.code] && (
                  <FaFileExcel className="file-icon" title="File ready" />
                )}
              </div>

              {/* Right */}
              <div className="download-actions">
                {!files[item.code] ? (
                  <button
                    className="btn btn-generate"
                    onClick={() => generateFile(item.code)}
                    disabled={loading === item.code}
                  >
                    <FaCog className={`spin-icon ${loading === item.code ? "spinning" : ""}`} />
                    {loading === item.code ? "Generating…" : "Generate"}
                  </button>

                ) : (
                  <>
                    <button
                      className="btn btn-download"
                      onClick={() => downloadFile(files[item.code], item.code)}
                    >
                      <FaDownload />
                      Download
                    </button>


                    <button
                      className="btn btn-copy"
                      onClick={() => copyUrl(files[item.code], item.code)}
                    >
                      <FaCopy />
                      Copy URL
                    </button>

                  </>
                )}
              </div>

            </div>
          ))}
        </div>
      </div>

      {/* ================= CONFIRM MODAL ================= */}
      {confirmType && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <h3>Are you sure?</h3>

            <p>
              {confirmType === "currency"
                ? "This will sync currency data and may take several times."
                : confirmType === "product" ? "This will sync a large number of products and may take a long time."
                  : "This will sync a abandoned checkout and may take a time."}
            </p>

            <div className="confirm-actions">
              <button
                className="btn btn-cancel"
                onClick={() => setConfirmType(null)}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={() => {
                  setConfirmType(null);
                  confirmType === "currency" ? handleGetCurrency()
                    : confirmType === "product" ? handleGetProduct()
                      : handleGetAbandonedCheckouts()
                }}
              >
                Yes, Continue
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
